import React, { useEffect, useState } from 'react';
import CascaDeModal from './shared/CascaDeModal';
import AvisoDeCotasDivergentes from './AvisoDeCotasDivergentes';
import { rateioService } from '../services/api';
import { esquecerRateio, useRateio } from '../hooks/useRateio';
import { ComRateio, TEXTO_DO_CRITERIO, fmtPercentual } from '../utils/rateioDoGrupo';
import { CotasDivergentes, lerCotasDivergentes } from '../utils/cotasDivergentes';
import { SimulacaoDaDivisao, produtosLadoALado, valorNoItem } from '../utils/redividir';
import { TONE_BORDER, TONE_SURFACE, TONE_TEXT } from '../utils/statusColors';

/**
 * REDIVIDIR: a base mudou e a divisão vigente foi acordada com outra contagem.
 * Simula (não grava), mostra vigente × calculado por empresa e por produto, exige a
 * observação e grava a divisão calculada pela base de hoje. Regras e contrato em
 * utils/redividir.ts.
 */

const brl = (v: number) => Number(v || 0).toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
const competenciaCorrente = () => {
  const d = new Date(new Date().toLocaleString('en-US', { timeZone: 'America/Sao_Paulo' }));
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
};
const corDoDelta = (d: number) => (d < 0 ? TONE_TEXT.danger : d > 0 ? TONE_TEXT.open : 'text-slate-400');
const comSinal = (d: number) => `${d > 0 ? '+' : d < 0 ? '−' : ''}R$ ${brl(Math.abs(d))}`;

interface Props {
  rateio: ComRateio;
  nome: (clientId: number) => string;
  onClose: () => void;
  onGravado: () => void;
}

export default function RedividirRateio({ rateio, nome, onClose, onGravado }: Props) {
  const [sim, setSim] = useState<SimulacaoDaDivisao | null>(null);
  const [erroDaSimulacao, setErroDaSimulacao] = useState<string | null>(null);
  const [competencia, setCompetencia] = useState(competenciaCorrente());
  const [observacao, setObservacao] = useState('');
  const [divergencia, setDivergencia] = useState<CotasDivergentes | null>(null);
  const [recalcular, setRecalcular] = useState(false);
  const [erro, setErro] = useState<string | null>(null);
  const [gravando, setGravando] = useState(false);

  // Pré-preenchido com o critério e os produtos atuais: pool = produtoIds, base = produtoIdsBase.
  const corpoDaSimulacao = {
    criterio: rateio.criterio,
    produtoIds: rateio.produtoIds,
    produtoIdsBase: rateio.produtoIdsBase,
    descontosSeguemRateio: rateio.descontosSeguemRateio,
  };

  const simular = async () => {
    setErroDaSimulacao(null);
    try {
      setSim(await rateioService.simularDivisao(rateio.matrizId, corpoDaSimulacao));
    } catch (e: any) {
      setErroDaSimulacao(e?.response?.data?.message || e?.message || 'Não foi possível simular a divisão.');
    }
  };

  // Sem as listas (versão que não as registrou), não há o que simular: nada de usar as de hoje.
  const semListas = !rateio.produtoIds || !rateio.produtoIdsBase;
  useEffect(() => { if (!semListas) simular(); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const gravar = async () => {
    if (!sim) return;
    setGravando(true);
    setErro(null);
    try {
      await rateioService.salvar(
        rateio.matrizId,
        {
          competenciaInicio: competencia,
          poolParaConfirmar: sim.poolParaConfirmar,
          // Eco da simulação NA TELA (backend #320): é a prova de que o que se grava é o que foi visto.
          // As entradas são as MESMAS da simulação, e sem baseDoPercentual (ver utils/redividir.ts).
          ...(sim.impressaoDaSimulacao ? { impressaoParaConfirmar: sim.impressaoDaSimulacao } : {}),
          criterio: corpoDaSimulacao.criterio,
          produtoIds: corpoDaSimulacao.produtoIds,
          produtoIdsBase: corpoDaSimulacao.produtoIdsBase,
          descontosSeguemRateio: corpoDaSimulacao.descontosSeguemRateio,
          observacao: observacao.trim(),
        },
        { recalcularCotas: !!divergencia && recalcular },
      );
      esquecerRateio();
      onGravado();
    } catch (e: any) {
      const d = lerCotasDivergentes(e);
      if (d) { setDivergencia(d); setRecalcular(false); }
      else {
        const msg = e?.response?.data?.message || e?.response?.data?.mensagem || e?.message || 'Erro ao gravar';
        setErro(Array.isArray(msg) ? msg.join(' ') : String(msg));
        // Base mudou desde a simulação, ou o conjunto gravado não é o simulado: o que está na tela
        // não vale mais. Simula de novo em vez de insistir.
        if (/base mudou desde a simula|conjunto que esta sendo gravado nao e o que foi simulado/i.test(String(msg))) simular();
      }
    } finally {
      setGravando(false);
    }
  };

  const bloqueado = gravando || !sim || !sim.somaFecha || !competencia || !observacao.trim() || (!!divergencia && !recalcular);
  const porEmpresa = rateio.linhas.map((l) => {
    const c = sim?.linhas.find((x) => x.clientId === l.clientId);
    const depois = c ? Number(c.depois) : l.valorCalculado;
    return { clientId: l.clientId, pctVig: l.percentualVigente, pctCalc: c ? c.percentual : l.percentualCalculado, vigente: l.valorCobrado, calculado: depois, delta: Math.round((depois - l.valorCobrado) * 100) / 100 };
  });
  const produtos = produtosLadoALado(rateio.itens?.vigente || [], sim?.itens || rateio.itens?.calculada || []);
  const th = 'text-[10px] uppercase tracking-wider text-slate-500 font-medium py-1';

  return (
    // Observação digitada: não fecha ao clicar fora nem no Esc.
    <CascaDeModal
      largura='lg'
      titulo='Redividir o rateio'
      subtitulo={`matriz #${rateio.matrizId} · ${TEXTO_DO_CRITERIO[rateio.criterio]} · compartilhado R$ ${brl(sim?.pool ?? rateio.pool)}`}
      onClose={onClose}
      topo={<p className='px-5 pt-3 text-sm text-slate-400'>A base mudou. Isto grava a divisão calculada pela base de hoje e muda o que as empresas do grupo pagam a partir da competência informada.</p>}
      corpoClassName='p-5 space-y-4'
      rodape={
        <>
          <span className='text-xs text-slate-500'>Grava uma versão nova do rateio. Não altera faturas já emitidas.</span>
          <div className='ml-auto flex gap-2'>
            <button type='button' onClick={onClose} className='px-4 py-2 rounded-lg bg-slate-700 hover:bg-slate-600 text-sm text-white'>Cancelar</button>
            <button type='button' onClick={gravar} disabled={bloqueado} className='px-4 py-2 rounded-lg bg-primary hover:bg-primary-strong text-sm font-medium text-white disabled:opacity-50'>
              {gravando ? 'Gravando…' : 'Gravar a divisão nova'}
            </button>
          </div>
        </>
      }
    >
      {semListas && <div className={`rounded-lg border p-3 text-sm ${TONE_SURFACE.warning} ${TONE_BORDER.warning} ${TONE_TEXT.warning}`} role='alert'>{rateio.listasNaoRegistradasMotivo || 'Esta versão não registrou quais produtos compunham o compartilhado e a base: não há o que redividir sobre ela.'}</div>}
      {erroDaSimulacao && <div className={`rounded-lg border p-3 text-sm ${TONE_SURFACE.danger} ${TONE_BORDER.danger} ${TONE_TEXT.danger}`} role='alert'>{erroDaSimulacao}</div>}
      {erro && <div className={`rounded-lg border p-3 text-sm ${TONE_SURFACE.danger} ${TONE_BORDER.danger} ${TONE_TEXT.danger}`} role='alert'>{erro}</div>}
      {sim && !sim.somaFecha && (
        <div className={`rounded-lg border p-3 text-sm ${TONE_SURFACE.danger} ${TONE_BORDER.danger} ${TONE_TEXT.danger}`} role='alert'>A divisão calculada não fecha 100%. {sim.motivoDoCalculo}</div>
      )}
      {divergencia && <AvisoDeCotasDivergentes divergencia={divergencia} recalcular={recalcular} onRecalcular={setRecalcular} />}

      <div>
        <p className='text-xs font-medium text-slate-400 mb-1'>Por empresa</p>
        <table className='w-full text-sm'>
          <thead><tr><th className={`${th} text-left`}>Empresa</th><th className={`${th} text-right`}>Cobrado hoje</th><th className={`${th} text-right`}>Calculado</th><th className={`${th} text-right`}>Diferença</th></tr></thead>
          <tbody>
            {porEmpresa.map((e) => (
              <tr key={e.clientId} className='border-t border-central-borda'>
                <td className='py-1.5 text-slate-200'>{nome(e.clientId)}</td>
                {/* O percentual é o ACORDADO e o valor é o COBRADO: dois números, dois rótulos (contrato de 05/10/2026). */}
                <td className='py-1.5 text-right tabular-nums text-slate-300'>R$ {brl(e.vigente)}<span className='block text-[11px] text-slate-500'>acordado {fmtPercentual(e.pctVig)}</span></td>
                <td className='py-1.5 text-right tabular-nums text-slate-300'>{fmtPercentual(e.pctCalc)} · R$ {brl(e.calculado)}</td>
                <td className={`py-1.5 text-right tabular-nums font-semibold ${corDoDelta(e.delta)}`} data-testid={`redividir-delta-${e.clientId}`}>{comSinal(e.delta)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {produtos.length > 0 && (
        <div>
          <p className='text-xs font-medium text-slate-400 mb-1'>Por produto do pool</p>
          <table className='w-full text-xs'>
            <thead><tr><th className={`${th} text-left`}>Produto</th>{rateio.linhas.map((l) => <th key={l.clientId} className={`${th} text-right`}>{nome(l.clientId)} (vigente → calculado)</th>)}</tr></thead>
            <tbody>
              {produtos.map((p) => (
                <tr key={p.productId} className='border-t border-central-borda'>
                  <td className='py-1 text-slate-300'>{p.nome}</td>
                  {rateio.linhas.map((l) => (
                    <td key={l.clientId} className='py-1 text-right tabular-nums text-slate-400'>
                      R$ {brl(valorNoItem(p.vigente, l.clientId))} → <span className='text-slate-200'>R$ {brl(valorNoItem(p.calculada, l.clientId))}</span>
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <div className='grid grid-cols-3 gap-3'>
        <div>
          <label className='block text-xs text-slate-400 mb-1' htmlFor='rd-comp'>A partir da competência *</label>
          <input id='rd-comp' type='month' value={competencia} onChange={(e) => setCompetencia(e.target.value)} className='w-full px-2 py-1.5 bg-slate-950 border border-slate-700 rounded-md text-sm text-white' />
        </div>
        <div className='col-span-2'>
          <label className='block text-xs text-slate-400 mb-1' htmlFor='rd-obs'>Observação * <span className='text-slate-500'>(isso muda o que as empresas pagam: diga por quê)</span></label>
          <textarea id='rd-obs' rows={2} value={observacao} onChange={(e) => setObservacao(e.target.value)} placeholder='Ex.: PRÉ-FABRICADOS passou de 14 para 15 ramais em 10/2026' className='w-full px-3 py-2 bg-slate-950 border border-slate-700 rounded-lg text-sm text-white' />
        </div>
      </div>
    </CascaDeModal>
  );
}

/** Invólucro para a tela de grupos: lê o rateio do grupo (mesmo cache do painel) e abre o modal. */
export function RedividirDoGrupo({ clientId, nome, onClose, onGravado }: { clientId: number; nome: (clientId: number) => string; onClose: () => void; onGravado: () => void }) {
  const { rateio } = useRateio(clientId);
  if (!rateio || rateio.temRateio === false) return null;
  return <RedividirRateio rateio={rateio as ComRateio} nome={nome} onClose={onClose} onGravado={onGravado} />;
}
